import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { InsolvencyFeedResponse } from '@marstein-radar/shared';
import { useAuth } from '../auth/AuthContext';
import { tableStyle, thStyle, tdStyle, buttonStyle } from '../styles';

const EVENT_TYPE_LABEL: Record<string, string> = {
  konkurz_restrukturalizacia: 'Konkurz/reštrukturalizácia',
  konkurz_vyrovnanie: 'Konkurz/vyrovnanie',
  likvidacia: 'Likvidácia',
  znizenie_imania: 'Zníženie základného imania',
};

// Fáza 1 inzertnej/insolvenčnej sekcie (2026-07-15, user nápad — inšpirované ľuďmi,
// ktorých pozná z konkurzných konaní, kde sa majetok speňažuje). Toto NIE JE
// marketplace ani zoznam vecí na predaj — overené naživo priamym SELECTom nad
// insolvency_events.description (74k riadkov), že dáta z Obchodného vestníka
// obsahujú len súdnu spisovú značku, žiadny majetok, kontakt na správcu, ani status
// "stále aktívne vs. uzavreté". Táto stránka je preto čestný, filtrovateľný prehľad
// firiem s insolvenčnou udalosťou — Fáza 2 (skutočné inzeráty od predajcov) je
// samostatný, väčší plán na neskôr. Kostra kopíruje SearchPage.tsx (jediný etablovaný
// vzor filtrovateľnej stránkovanej tabuľky v appke).
export function InsolvencyFeedPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const { data: regions } = useQuery<string[]>({
    queryKey: ['regions'],
    queryFn: async () => {
      const res = await fetch('/api/v1/companies/regions');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    staleTime: Infinity,
  });

  // Týž. 21 — predvyplnenie z odkazu uloženého "sledovaného záujmu" (AccountPage →
  // /insolvencie?region=..&eventType=..), viď WatchlistInterestsSection.
  const [eventType, setEventType] = useState(searchParams.get('eventType') ?? '');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState(searchParams.get('region') ?? '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Týž. 21 (prieskum "čo chýba" 2026-07-18) — fulltext nad textom uznesenia
  // (heading/decision/announcement/advice), doplnený tou istou session.
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [submittedKey, setSubmittedKey] = useState<string | null>('initial');
  const [saveLabel, setSaveLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const { data, isFetching } = useQuery<InsolvencyFeedResponse>({
    queryKey: ['insolvency-feed', submittedKey, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (eventType) params.set('eventType', eventType);
      if (city.trim()) params.set('city', city.trim());
      if (region) params.set('region', region);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (q.trim()) params.set('q', q.trim());
      params.set('page', String(page));
      const res = await fetch(`/api/v1/insolvency/feed?${params.toString()}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: submittedKey !== null,
  });

  function submit(): void {
    setPage(1);
    setSubmittedKey(`${eventType}|${city}|${region}|${dateFrom}|${dateTo}|${q}|${Date.now()}`);
  }

  // Týž. 21 — uloženie aktuálneho filtra (kraj/typ) ako "sledovaný záujem" v účte,
  // viď WatchlistInterestsSection na AccountPage.tsx. `keyword` tu zatiaľ nie je vo
  // filter-grid (feed nemá fulltext podľa mena firmy), preto sa neposiela.
  async function saveInterest(): Promise<void> {
    if (!saveLabel.trim()) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const res = await fetch('/api/v1/watchlist/interests', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          label: saveLabel.trim(),
          region: region || undefined,
          eventType: eventType || undefined,
        }),
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      setSaveLabel('');
      setSaveMessage('Uložené — nájdeš to v účte pod "Sledované záujmy".');
      await qc.invalidateQueries({ queryKey: ['watchlist-interests'] });
    } catch (err) {
      setSaveMessage(`Nepodarilo sa uložiť: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <main className="page">
      <div className="page-header">
        <h1>Insolvencie</h1>
        <p>
          Firmy so zaznamenanou udalosťou v Obchodnom vestníku — konkurz, reštrukturalizácia,
          likvidácia.
        </p>
      </div>

      <div className="fin-panel" style={{ padding: '14px 20px', marginBottom: '1.5rem' }}>
        Toto je zoznam firiem so zaznamenanou udalosťou v Obchodnom vestníku, <strong>nie</strong>{' '}
        potvrdenie, že konanie stále prebieha — databáza neobsahuje status "aktívne/uzavreté",
        kontakt na správcu ani zoznam speňažovaného majetku. Tieto údaje treba dohľadať priamo v{' '}
        <a href="https://www.justice.gov.sk/pod/ov.nsf/" target="_blank" rel="noreferrer">
          Obchodnom vestníku
        </a>{' '}
        alebo cez ORSR podľa spisovej značky uvedenej pri firme nižšie.
      </div>

      <div className="search-filter-panel">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="filter-grid">
            <label className="filter-field">
              <span>Typ udalosti</span>
              <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
                <option value="">Všetky typy</option>
                {Object.entries(EVENT_TYPE_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="filter-field">
              <span>Mesto / obec</span>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="napr. Košice"
              />
            </label>
            <label className="filter-field">
              <span>Kraj</span>
              <select value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="">Všetky kraje</option>
                {regions?.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <label className="filter-field filter-field-narrow">
              <span>Dátum od</span>
              <input value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} type="date" />
            </label>
            <label className="filter-field filter-field-narrow">
              <span>Dátum do</span>
              <input value={dateTo} onChange={(e) => setDateTo(e.target.value)} type="date" />
            </label>
            <label className="filter-field filter-field-wide">
              <span>Hľadať v texte uznesenia</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="napr. byt, pozemok, nehnuteľnosť..."
              />
            </label>
          </div>
          <button type="submit" className="search-filter-submit">
            Hľadať
          </button>
        </form>
        {user && (
          <div
            style={{
              display: 'flex',
              gap: '0.6rem',
              alignItems: 'center',
              marginTop: '0.9rem',
              flexWrap: 'wrap',
            }}
          >
            <input
              value={saveLabel}
              onChange={(e) => setSaveLabel(e.target.value)}
              placeholder="Názov záujmu (napr. Nehnuteľnosti v BA kraji)"
              style={{ flex: '1 1 260px' }}
            />
            <button
              type="button"
              onClick={() => void saveInterest()}
              disabled={saving || !saveLabel.trim()}
              style={buttonStyle}
            >
              {saving ? 'Ukladám...' : 'Uložiť ako sledovaný záujem'}
            </button>
            {saveMessage && (
              <span className="hint" style={{ flexBasis: '100%' }}>
                {saveMessage}
              </span>
            )}
          </div>
        )}
      </div>

      {isFetching && !data && (
        <div className="fin-panel">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="skel-row" key={i}>
              <div>
                <div className="skel line1" />
                <div className="skel line2" />
              </div>
              <div className="skel pill" />
              <div className="skel num" />
            </div>
          ))}
        </div>
      )}
      {data && data.results.length === 0 && (
        <div className="fin-panel" style={{ padding: '18px 22px' }}>
          Žiadne udalosti nenájdené pre zadané filtre.
        </div>
      )}
      {data && data.results.length > 0 && (
        <>
          <div className="section-title">
            <h2>Výsledky</h2>
            <span className="hint">{data.total.toLocaleString('sk-SK')} udalostí spolu</span>
          </div>
          <div className="overflow-x">
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Dátum</th>
                  <th style={thStyle}>Typ udalosti</th>
                  <th style={thStyle}>Firma</th>
                  <th style={thStyle}>Mesto</th>
                  <th style={thStyle}>Spisová značka</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/firma/${r.ico}`)}
                    style={{ cursor: 'pointer' }}
                    className="search-result-row"
                  >
                    <td style={tdStyle} className="num">
                      {r.eventDate ?? '—'}
                    </td>
                    <td style={tdStyle}>{EVENT_TYPE_LABEL[r.eventType] ?? r.eventType}</td>
                    <td style={tdStyle}>
                      {r.companyName ?? '—'}
                      <span className="num" style={{ color: 'var(--slate)', fontSize: '0.8rem' }}>
                        {' '}
                        · IČO {r.ico}
                      </span>
                    </td>
                    <td style={tdStyle}>{r.city ?? '—'}</td>
                    <td style={tdStyle}>{r.description ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div
              style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.9rem' }}
            >
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ ...buttonStyle, opacity: page <= 1 ? 0.5 : 1 }}
              >
                ← Predošlá
              </button>
              <span style={{ color: 'var(--slate)', fontSize: '0.85rem' }}>
                Strana {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ ...buttonStyle, opacity: page >= totalPages ? 0.5 : 1 }}
              >
                Ďalšia →
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
